import React, { useState, useEffect } from 'react';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Building2, Upload, FileSpreadsheet, Wand2, Check, ArrowRight, 
  ArrowLeft, Download, Layers, Loader2, Plus, 
  AlertCircle, CheckCircle2, HelpCircle, Save, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const WIZARD_STEPS = [
  { id: 1, title: 'Select Bank', description: 'Choose or add your bank' },
  { id: 2, title: 'Upload Template', description: "Upload bank's bid sheet format" },
  { id: 3, title: 'Enter Costs', description: 'Paste or enter your project costs' },
  { id: 4, title: 'AI Mapping', description: 'AI maps costs to bank categories' },
  { id: 5, title: 'Save Bid Sheet', description: 'Review and save as pending bid sheet' },
];

export default function BidSheetAIWizard({ user, open, onOpenChange, onBidSheetCreated }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedBank, setSelectedBank] = useState(null);
  const [costsText, setCostsText] = useState('');
  const [mappings, setMappings] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bidSheetInfo, setBidSheetInfo] = useState({ 
    name: '', 
    borrower_name: '', 
    property_address: '' 
  });
  
  // Add bank state
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [newBankName, setNewBankName] = useState('');
  const [newBankLogo, setNewBankLogo] = useState('');

  useEffect(() => {
    if (open && user?.id) {
      loadBanks();
    }
  }, [open, user]);

  const loadBanks = async () => {
    try {
      setLoading(true);
      const res = await apiService.getBankTemplates(user.id);
      setBanks(res.data || []);
    } catch (err) {
      console.error('Failed to load banks:', err);
      toast.error('Failed to load bank templates');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBank = async () => {
    if (!newBankName.trim()) {
      toast.error('Please enter a bank name');
      return;
    }
    
    try {
      const res = await apiService.createBankTemplate({
        user_id: user.id,
        bank_name: newBankName.trim(),
        logo_url: newBankLogo.trim() || null
      });
      toast.success('Bank added successfully');
      setAddBankOpen(false);
      setNewBankName('');
      setNewBankLogo('');
      loadBanks();
      
      // Auto-select the new bank and start wizard
      setSelectedBank(res.data);
      setCurrentStep(2);
    } catch (err) {
      toast.error('Failed to add bank');
    }
  };

  const handleUploadTemplate = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedBank) return;
    
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiService.uploadBankTemplateFile(selectedBank.id, formData);
      toast.success(`Template uploaded! ${res.data.category_count || 0} categories detected.`);
      
      // Update selected bank with new categories
      setSelectedBank({
        ...selectedBank,
        detected_categories: res.data.detected_categories,
        template_file_id: res.data.file_id
      });
      
      loadBanks();
      setCurrentStep(3);
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload template');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAIMapping = async () => {
    if (!costsText.trim()) {
      toast.error('Please enter your costs');
      return;
    }
    
    setIsProcessing(true);
    setCurrentStep(4); // Show loading state
    try {
      const res = await apiService.aiMapCostsToBankTemplate(selectedBank.id, costsText);
      setMappings(res.data.mappings || []);
      toast.success('AI mapping complete!');
      setCurrentStep(5);
    } catch (err) {
      console.error('AI mapping failed:', err);
      toast.error('AI mapping failed. Please try again.');
      setCurrentStep(3); // Go back to costs entry
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveBidSheet = async () => {
    if (!bidSheetInfo.name.trim()) {
      toast.error('Please enter a name for the bid sheet');
      return;
    }
    
    if (!mappings.length) {
      toast.error('No mappings to save');
      return;
    }
    
    setIsProcessing(true);
    try {
      // Calculate total
      const total = mappings.reduce((sum, m) => sum + (m.user_amount || 0), 0);
      
      // Create bid sheet with AI mappings
      const res = await apiService.createBidSheet({
        user_id: user.id,
        name: bidSheetInfo.name.trim(),
        borrower_name: bidSheetInfo.borrower_name || '',
        property_address: bidSheetInfo.property_address || '',
        bank_template_id: selectedBank.id,
        ai_mappings: mappings,
        original_costs_text: costsText,
        status: 'draft'
      });
      
      // Update total cost
      await apiService.updateBidSheet(res.data.id, { total_cost: total });
      
      toast.success('Bid sheet saved successfully!');
      resetWizard();
      onOpenChange(false);
      
      if (onBidSheetCreated) {
        onBidSheetCreated(res.data);
      }
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save bid sheet');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportToBank = async () => {
    if (!mappings.length) {
      toast.error('No mappings to export');
      return;
    }
    
    setIsProcessing(true);
    try {
      const res = await apiService.exportMappedCosts(selectedBank.id, mappings, {
        borrower: bidSheetInfo.borrower_name,
        address: bidSheetInfo.property_address
      });
      
      // Download the file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bid_sheet_${selectedBank.bank_name.replace(/\s+/g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Bid sheet exported successfully!');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setSelectedBank(null);
    setCostsText('');
    setMappings([]);
    setBidSheetInfo({ name: '', borrower_name: '', property_address: '' });
  };

  const calculateTotals = () => {
    const total = mappings.reduce((sum, m) => sum + (m.user_amount || 0), 0);
    const contingency = total * 0.05;
    return { total, contingency, grandTotal: total + contingency };
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-6 px-2">
      {WIZARD_STEPS.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
              currentStep === step.id 
                ? 'bg-blue-600 text-white ring-4 ring-blue-100' 
                : currentStep > step.id
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-200 text-slate-500'
            }`}>
              {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
            </div>
            <span className={`text-xs mt-1 text-center max-w-[80px] ${
              currentStep === step.id ? 'text-blue-600 font-medium' : 'text-slate-500'
            }`}>
              {step.title}
            </span>
          </div>
          {index < WIZARD_STEPS.length - 1 && (
            <div className={`flex-1 h-1 mx-2 rounded ${
              currentStep > step.id ? 'bg-green-500' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <Building2 className="w-12 h-12 mx-auto text-blue-600 mb-2" />
        <h3 className="text-lg font-semibold">Select Your Bank</h3>
        <p className="text-sm text-slate-500">Choose an existing bank or add a new one</p>
      </div>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : banks.length > 0 ? (
        <div className="grid gap-3 max-h-[300px] overflow-y-auto">
          {banks.map(bank => (
            <Card 
              key={bank.id}
              className={`cursor-pointer transition-all hover:border-blue-300 ${
                selectedBank?.id === bank.id ? 'border-blue-500 bg-blue-50' : ''
              }`}
              onClick={() => {
                setSelectedBank(bank);
                if (bank.template_file_id && bank.detected_categories?.length) {
                  setCurrentStep(3);
                } else {
                  setCurrentStep(2);
                }
              }}
              data-testid={`bank-card-${bank.id}`}
            >
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  {bank.logo_url ? (
                    <img src={bank.logo_url} alt={bank.bank_name} className="w-10 h-10 rounded object-contain" />
                  ) : (
                    <Building2 className="w-10 h-10 text-slate-400" />
                  )}
                  <div>
                    <p className="font-medium">{bank.bank_name}</p>
                    <p className="text-xs text-slate-500">
                      {bank.detected_categories?.length 
                        ? `${bank.detected_categories.length} categories mapped`
                        : 'No template uploaded'}
                    </p>
                  </div>
                </div>
                {bank.template_file_id && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <FileSpreadsheet className="w-3 h-3 mr-1" /> Template Ready
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-500">
          <p>No banks added yet. Add your first bank to get started.</p>
        </div>
      )}
      
      <Button 
        variant="outline" 
        className="w-full mt-4"
        onClick={() => setAddBankOpen(true)}
        data-testid="add-bank-btn"
      >
        <Plus className="w-4 h-4 mr-2" /> Add New Bank
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <Upload className="w-12 h-12 mx-auto text-blue-600 mb-2" />
        <h3 className="text-lg font-semibold">Upload Bank Template</h3>
        <p className="text-sm text-slate-500">
          Upload the Excel bid sheet format from <strong>{selectedBank?.bank_name}</strong>
        </p>
      </div>
      
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
        <input
          type="file"
          id="template-upload"
          className="hidden"
          accept=".xlsx,.xls,.xlsm"
          onChange={handleUploadTemplate}
          disabled={isProcessing}
          data-testid="template-upload-input"
        />
        <label htmlFor="template-upload" className="cursor-pointer">
          {isProcessing ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-2" />
              <p className="text-slate-600">Processing template...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <FileSpreadsheet className="w-10 h-10 text-slate-400 mb-2" />
              <p className="text-slate-600 mb-1">Click to upload or drag and drop</p>
              <p className="text-xs text-slate-400">Excel files (.xlsx, .xls, .xlsm)</p>
            </div>
          )}
        </label>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
          <HelpCircle className="w-4 h-4" /> What happens next?
        </h4>
        <ul className="text-sm text-blue-700 space-y-1 ml-6 list-disc">
          <li>AI will scan the template and extract all cost categories</li>
          <li>Categories like "101 Excavation", "201 Framing" will be detected</li>
          <li>You'll be able to map your costs to these categories</li>
        </ul>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <Layers className="w-12 h-12 mx-auto text-blue-600 mb-2" />
        <h3 className="text-lg font-semibold">Enter Your Project Costs</h3>
        <p className="text-sm text-slate-500">
          Paste your costs below. Include item names and amounts.
        </p>
      </div>
      
      {selectedBank?.detected_categories?.length > 0 && (
        <div className="bg-green-50 p-3 rounded-lg mb-4">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <strong>{selectedBank.detected_categories.length}</strong> bank categories detected and ready for mapping
          </p>
        </div>
      )}
      
      <div>
        <Label>Project Costs</Label>
        <Textarea
          value={costsText}
          onChange={(e) => setCostsText(e.target.value)}
          placeholder={`Paste your costs here. Example format:

HVAC - $99,998
Electrical - $88,815
Plumbing - $118,966.19
Roofing Labor - $9,260
Concrete - $173,333
...`}
          className="min-h-[200px] font-mono text-sm"
          data-testid="costs-textarea"
        />
        <p className="text-xs text-slate-500 mt-1">
          Tip: Include item names and dollar amounts. AI will parse and map them automatically.
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Borrower Name</Label>
          <Input 
            value={bidSheetInfo.borrower_name}
            onChange={(e) => setBidSheetInfo({...bidSheetInfo, borrower_name: e.target.value})}
            placeholder="John Smith"
            data-testid="borrower-input"
          />
        </div>
        <div>
          <Label>Property Address</Label>
          <Input 
            value={bidSheetInfo.property_address}
            onChange={(e) => setBidSheetInfo({...bidSheetInfo, property_address: e.target.value})}
            placeholder="123 Main St, City, State"
            data-testid="address-input"
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4 text-center py-8">
      <Wand2 className="w-16 h-16 mx-auto text-blue-600 animate-pulse" />
      <h3 className="text-xl font-semibold">AI is Mapping Your Costs</h3>
      <p className="text-slate-500">
        Analyzing your costs and matching them to {selectedBank?.bank_name}'s categories...
      </p>
      <Loader2 className="w-8 h-8 mx-auto text-blue-600 animate-spin mt-4" />
    </div>
  );

  const renderStep5 = () => {
    const { total, contingency, grandTotal } = calculateTotals();
    
    return (
      <div className="space-y-4">
        <div className="text-center mb-4">
          <CheckCircle2 className="w-12 h-12 mx-auto text-green-600 mb-2" />
          <h3 className="text-lg font-semibold">Review & Save</h3>
          <p className="text-sm text-slate-500">
            {mappings.length} items mapped to bank categories
          </p>
        </div>
        
        {/* Bid Sheet Name */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <Label className="text-blue-800 font-medium">Bid Sheet Name *</Label>
          <Input 
            value={bidSheetInfo.name}
            onChange={(e) => setBidSheetInfo({...bidSheetInfo, name: e.target.value})}
            placeholder="e.g., 123 Main St - Bank Submission"
            className="mt-2 bg-white"
            data-testid="bidsheet-name-input"
          />
        </div>
        
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-slate-50">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Subtotal</p>
              <p className="text-lg font-bold text-slate-800">${total.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-amber-600">5% Contingency</p>
              <p className="text-lg font-bold text-amber-700">${contingency.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-green-600">Grand Total</p>
              <p className="text-lg font-bold text-green-700">${grandTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Mappings Table */}
        <div className="border rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="text-left p-2 font-medium">Bank Code</th>
                <th className="text-left p-2 font-medium">Your Item</th>
                <th className="text-right p-2 font-medium">Amount</th>
                <th className="text-center p-2 font-medium">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {mappings.map((m, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="p-2">
                    <Badge variant="outline" className="font-mono">{m.bank_code}</Badge>
                    <span className="text-xs text-slate-500 ml-2">{m.bank_category}</span>
                  </td>
                  <td className="p-2">{m.user_item}</td>
                  <td className="p-2 text-right font-medium">
                    ${(m.user_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </td>
                  <td className="p-2 text-center">
                    <Badge className={
                      m.confidence === 'high' ? 'bg-green-100 text-green-700' :
                      m.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }>
                      {m.confidence}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Actions */}
        <div className="grid grid-cols-2 gap-4 pt-4">
          <Button 
            onClick={handleExportToBank}
            variant="outline"
            className="w-full"
            disabled={isProcessing}
            data-testid="export-excel-btn"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export to Excel
          </Button>
          <Button 
            onClick={handleSaveBidSheet}
            className="w-full bg-green-600 hover:bg-green-700"
            disabled={isProcessing || !bidSheetInfo.name.trim()}
            data-testid="save-bidsheet-btn"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save as Bid Sheet
          </Button>
        </div>
        
        <p className="text-xs text-slate-500 text-center">
          Saving creates a pending bid sheet for bank approval. You can export to the bank's exact format later.
        </p>
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen) resetWizard();
        onOpenChange(isOpen);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-blue-600" />
              AI Cost Mapping Wizard
            </DialogTitle>
            <DialogDescription>
              Map your construction costs to {selectedBank?.bank_name || "bank"} format using AI
            </DialogDescription>
          </DialogHeader>
          
          {renderStepIndicator()}
          
          <div className="min-h-[300px]">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
          </div>
          
          {/* Navigation */}
          {currentStep !== 4 && (
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1 || isProcessing}
                data-testid="wizard-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              
              {currentStep < 4 && (
                <Button
                  onClick={() => {
                    if (currentStep === 3) {
                      handleAIMapping();
                    } else if (currentStep === 1 && selectedBank) {
                      if (selectedBank.template_file_id) {
                        setCurrentStep(3);
                      } else {
                        setCurrentStep(2);
                      }
                    } else {
                      setCurrentStep(currentStep + 1);
                    }
                  }}
                  disabled={
                    isProcessing || 
                    (currentStep === 1 && !selectedBank) ||
                    (currentStep === 3 && !costsText.trim())
                  }
                  data-testid="wizard-next-btn"
                >
                  {currentStep === 3 ? (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" /> Run AI Mapping
                    </>
                  ) : (
                    <>
                      Next <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Bank Dialog */}
      <Dialog open={addBankOpen} onOpenChange={setAddBankOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Bank</DialogTitle>
            <DialogDescription>
              Add a bank to configure for cost mapping
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={newBankName}
                onChange={(e) => setNewBankName(e.target.value)}
                placeholder="e.g., Hills Bank, Wells Fargo"
                data-testid="new-bank-name-input"
              />
            </div>
            <div>
              <Label>Logo URL (optional)</Label>
              <Input
                value={newBankLogo}
                onChange={(e) => setNewBankLogo(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddBankOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddBank} data-testid="confirm-add-bank-btn">
                Add Bank
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
